import React, { useEffect, useState } from "react";
import { getCommentNumber, getPostComment, getLikeNumber, likePost, savePost, addFollower, deleteFollower, check_followship, checkPostSave } from "../api";
import { isSameUser } from "../lib/utils";

import ShowComment from "./ShowComment";
import ToastMessage from "./ToastMessage";

const PostInfo = ({ post, user }) => {
  const [like, setLike] = useState(0);
  const [comment_numb, setComment_numb] = useState(0);
  const [showComment, setShowComment] = useState(false);
  const [allComments, setAllComments] = useState([]);
  const [isLiked, setIsLiked] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isFollowLoading, setIsFollowLoading] = useState(false);

  /* ================= LIKE STATE + COUNT ================= */

  const likeCount = async () => {
    if (!post?.id) return;

    try {
      const res = await getLikeNumber(post.id);

      setLike(Number(res.data?.count || 0));
      setIsLiked(res.data?.liked === true || Number(res.data?.liked) === 1);
    } catch (error) {
      console.error("Like count error:", error);
    }
  };

  /* ================= SAVE STATE ================= */

useEffect(() => {
  if (!post?.id) return;

  const getSaveStatus = async () => {
    try {
      const res = await checkPostSave(post.id);
      setIsSaved(res.data?.saved === true || Number(res.data?.saved) === 1);
    } catch (error) {
      console.error("Save status error:", error);
    }
  };

  getSaveStatus();
}, [post?.id]);

  /* ================= FOLLOW RELATIONSHIP ================= */

  useEffect(() => {
    if (!post?.creator_id || !user?.id || isSameUser(post.creator_id, user.id)) return;

    const getFollowStatus = async () => {
      try {
        const res = await check_followship(post.creator_id);
        setIsFollowing(Number(res.data?.a_follows_b) === 1);
      } catch (error) {
        console.error("Follow status error:", error);
      }
    };

    getFollowStatus();
  }, [post?.creator_id, user?.id]);

  /* ================= LIKE / UNLIKE ================= */

  const handleLike = async (id) => {
    if (!id) return;

    const previousLikedState = isLiked;
    const previousLikeCount = like;

    setIsLiked(!previousLikedState);
    setLike(previousLikedState ? Math.max(previousLikeCount - 1, 0) : previousLikeCount + 1);

    try {
      await likePost(id);
      await likeCount();
    } catch (error) {
      console.error("Like error:", error);
      setIsLiked(previousLikedState);
      setLike(previousLikeCount);
      ToastMessage(error?.response?.data?.message || "Unable to update like.");
    }
  };

  /* ================= FOLLOW / UNFOLLOW ================= */

  const handleFollow = async () => {
    if (!post?.creator_id || isFollowLoading) return;

    const previousState = isFollowing;

    setIsFollowing(!previousState);
    setIsFollowLoading(true);

    try {
      if (previousState) {
        const res = await deleteFollower(post.creator_id);
        ToastMessage(res.data.message);
      } else {
        const res = await addFollower(post.creator_id);
        ToastMessage(res.data.message);
      }
    } catch (error) {
      console.error("Follow error:", error);
      setIsFollowing(previousState);
      ToastMessage(error?.response?.data?.message || "Unable to update follow status.");
    } finally {
      setIsFollowLoading(false);
    }
  };

  /* ================= SAVE / UNSAVE ================= */

  const handleSave = async (id) => {
    if (!id) return;

    const previousState = isSaved;

    setIsSaved(!previousState);

    try {
      const res = await savePost(id);

      if (res.data?.message === "Post has been saved") setIsSaved(true);
      if (res.data?.message === "Post has been unsaved") setIsSaved(false);

      ToastMessage(res.data?.message);
    } catch (error) {
      console.error("Save error:", error);
      setIsSaved(previousState);
      ToastMessage(error?.response?.data?.message || "Unable to update saved post.");
    }
  };

  /* ================= COMMENT COUNT ================= */

  const comment_count = async () => {
    if (!post?.id) return;

    try {
      const res = await getCommentNumber(post.id);
      setComment_numb(Number(res.data?.count || 0));
    } catch (error) {
      console.error("Comment count error:", error);
    }
  };

  /* ================= GET COMMENTS ================= */

  const getAllComments = async (id) => {
    if (!id) return;

    try {
      const res = await getPostComment(id);
      setAllComments(res.data);
    } catch (error) {
      console.error("Get comments error:", error);
    }
  };

  /* ================= LOAD / POLL COUNTS ================= */

useEffect(() => {
  if (!post?.id) return;

  likeCount();
  comment_count();
}, [post?.id]);

  return (
    <>
      <div className="flex w-full items-center justify-between gap-2">
        {/* LEFT */}

        <div className="flex min-w-0 items-center gap-3 sm:gap-8">
          {/* LIKE */}

          <button type="button" onClick={() => handleLike(post?.id)} className="flex shrink-0 items-center gap-1 text-sm">
            <img src={isLiked ? "/assets/icons/liked.svg" : "/assets/icons/like.svg"} alt={isLiked ? "liked" : "like"} />
            <span className="text-center">{like > 0 ? like : ""} </span>
          </button>

          {/* COMMENTS */}

          <button type="button" className="flex shrink-0 items-center gap-1 text-sm" onClick={() => {
            setShowComment(true);
            getAllComments(post?.id);
          }}>
            <img src="/assets/icons/chat.svg" alt="comments" />
            <span>{comment_numb > 0 ? comment_numb : ""} <span className="hidden xs:inline">{comment_numb === 1 ? "comment" : "comments"}</span></span>
          </button>
        </div>

        {/* RIGHT */}

        <div className="flex shrink-0 items-center gap-2 sm:gap-6">
          {/* FOLLOW */}

          {!isSameUser(user?.id, post?.creator_id) && (
            <button type="button" onClick={handleFollow} disabled={isFollowLoading} className={`flex items-center gap-1 rounded-md border px-2 py-2 text-sm font-medium transition-all duration-200 sm:px-3 ${isFollowing ? "border-purple-200 bg-purple-50 text-purple-600 hover:bg-purple-100" : "border-gray-200 bg-white text-gray-600 hover:border-purple-200 hover:bg-purple-50 hover:text-purple-600"} ${isFollowLoading ? "cursor-not-allowed opacity-70" : ""}`}>
              <img src="/assets/icons/people.svg" className="h-5" alt="follow" />
              <span className="hidden sm:inline">{isFollowing ? "Unfollow" : "Follow"}</span>
            </button>
          )}

          {/* SAVE */}

          <button type="button" onClick={() => handleSave(post?.id)} className={`flex shrink-0 items-center gap-1 text-sm transition ${isSaved ? "font-medium text-purple-600" : "text-gray-700"}`}>
            <img src={isSaved ? "/assets/icons/saved.svg" : "/assets/icons/save.svg"} alt={isSaved ? "saved" : "save"} />
            <span className="hidden sm:inline">{isSaved ? "saved" : "save"}</span>
          </button>
        </div>
      </div>

      <ShowComment isOpen={showComment} comment_numb={comment_numb} isClose={() => setShowComment(false)} allComments={allComments} />
    </>
  );
};

export default PostInfo;